import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { toast } from "react-toastify";

import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";

const ESTATUS_ORDEN = [
  { _id: "all", name: "Todos los estatus" },
  { _id: "Creada", name: "Creada" },
  { _id: "Pendiente", name: "Pendiente" },
  { _id: "Autorizada", name: "Autorizada" },
  { _id: "Pagada", name: "Pagada" },
  { _id: "Parcial", name: "Parcial" },
  { _id: "Cerrada", name: "Cerrada" },
  { _id: "Rechazada", name: "Rechazada" },
];

const CONDICIONES_PAGO = [
  { _id: "all", name: "Todas" },
  { _id: "Contado", name: "Contado" },
  { _id: "Crédito", name: "Crédito" },
];

const ModalReporteCompras = ({ onClose }) => {
  const [proveedores, setProveedores] = useState([
    { _id: "all", name: "Todos los proveedores" },
  ]);
  const [clientes, setClientes] = useState([
    { _id: "all", name: "Todos los clientes" },
  ]);

  const [proveedorId, setProveedorId] = useState("all");
  const [estatus, setEstatus] = useState("all");
  const [clienteId, setClienteId] = useState("all");
  const [condicionPago, setCondicionPago] = useState("all");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resProv, resCli] = await Promise.all([
          axios.get(`${API}/prov`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/clientes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setProveedores([
          { _id: "all", name: "Todos los proveedores" },
          ...(Array.isArray(resProv.data) ? resProv.data : []),
        ]);
        setClientes([
          { _id: "all", name: "Todos los clientes" },
          ...(Array.isArray(resCli.data) ? resCli.data : []),
        ]);
      } catch {
        toast.error("Error al cargar proveedores o clientes");
      }
    };
    fetchData();
  }, [API, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "proveedor") setProveedorId(value);
    if (name === "estatus") setEstatus(value);
    if (name === "cliente") setClienteId(value);
    if (name === "condicion") setCondicionPago(value);
    if (name === "fechaInicio") setFechaInicio(value);
    if (name === "fechaFin") setFechaFin(value);
  };

  const handleGenerarReporte = async () => {
    if (!fechaInicio || !fechaFin) {
      toast.warning("Seleccione el rango de fechas");
      return;
    }

    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("fechaInicio", fechaInicio);
      params.append("fechaFin", fechaFin);
      if (proveedorId !== "all") params.append("proveedorId", proveedorId);
      if (estatus !== "all") params.append("estatus", estatus);
      if (clienteId !== "all") params.append("clienteId", clienteId);
      if (condicionPago !== "all")
        params.append("condicionPago", condicionPago);

      const res = await axios.get(
        `${API}/reportes/compras?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );

      const contentType = res.headers["content-type"];
      if (contentType.includes("application/pdf")) {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "reporte_compras.pdf";
        link.click();
        toast.success("Reporte de compras generado ✅");
        onClose();
      } else {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (json.success === false) toast.info(json.message);
        else toast.error("No se pudo generar el reporte");
      }
    } catch (err) {
      toast.error("Error al generar el reporte");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Reporte de Compras
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            <SelectField
              label="Proveedor"
              name="proveedor"
              value={proveedorId}
              options={proveedores}
              onChange={handleChange}
              displayFn={(p) =>
                p._id === "all" ? p.name : `[${p.nit}] ${p.name}`
              }
            />

            <SelectField
              label="Estatus"
              name="estatus"
              value={estatus}
              options={ESTATUS_ORDEN}
              onChange={handleChange}
            />

            <SelectField
              label="Cliente"
              name="cliente"
              value={clienteId}
              options={clientes}
              onChange={handleChange}
              displayFn={(c) =>
                c._id === "all" ? c.name : `[${c.nit}] ${c.name}`
              }
            />

            <SelectField
              label="Condición de pago"
              name="condicion"
              value={condicionPago}
              options={CONDICIONES_PAGO}
              onChange={handleChange}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InputField
                label="Fecha inicio"
                type="date"
                name="fechaInicio"
                value={fechaInicio}
                onChange={handleChange}
              />
              <InputField
                label="Fecha fin"
                type="date"
                name="fechaFin"
                value={fechaFin}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleGenerarReporte}
            disabled={isLoading}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            {isLoading ? "Generando..." : "Generar Reporte"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalReporteCompras;
